import { context, propagation, SpanKind, SpanStatusCode, trace, type Span } from '@opentelemetry/api';
import type { ResClient } from '@sysarb/resclient';
import { currentRoutePattern } from './pageLoadSpan';
import { templateRid } from './templating';
import { getTracer } from './tracer';

type ResMethod = (...args: never[]) => unknown;
type Operation = { action: string; rid: string; method?: string };

// resclient calls onBeforeTrace from _sendNow, which only runs when a request
// actually goes to the wire — client.get() short-circuits on its own cache and
// never reaches it. Creating the span there rather than in the wrappers is what
// keeps a cached read from minting a span for work that never happened.
//
// The hook is handed a numeric id and nothing else, so the wrappers leave the
// operation here for it to name from. _send -> _sendNow runs synchronously while
// connected, so the send consumes this before the wrapper clears it. A send
// issued while disconnected, or one resclient makes on its own (unsubscribe,
// resubscribe), finds nothing and is not traced at all.
let pendingOperation: Operation | null = null;
const openSpans = new Map<number, Span>();

const describing = <T>(action: string, rid: string, method: string | null, send: () => T): T => {
    pendingOperation = method === null ? { action, rid } : { action, rid, method };

    try {
        return send();
    } finally {
        pendingOperation = null;
    }
};

// Mirrors the RES subject the request is sent on: `call.<rid>.<method>` and
// `subscribe.<rid>`, with the tenant and any entity id templated out.
const resSpanName = ({ action, rid, method }: Operation): string =>
    `res.${action}.${templateRid(rid)}${method === undefined ? '' : `.${method}`}`;

/**
 * Opens a span for an outgoing RES request and returns the W3C headers resgate
 * reads off the message. Wired to ResgateManager's onBeforeTrace.
 */
export const startResSpan = (id: number): { traceparent: string; tracestate: string } => {
    const operation = pendingOperation;

    // A send nothing described is resclient's own unsubscribe/resubscribe housekeeping:
    // no rid, no action, and a reconnect emits one per cached resource in a single burst.
    if (operation === null) return { traceparent: '', tracestate: '' };

    const route = currentRoutePattern();

    const span = getTracer().startSpan(resSpanName(operation), {
        kind: SpanKind.CLIENT,
        attributes: {
            'res.resource': operation.rid,
            'res.action': operation.action,
            ...(route !== null ? { 'page.route': route } : {}),
        },
    });

    openSpans.set(id, span);

    // Inject from the new span rather than the ambient context: this span is the
    // parent resgate should continue from.
    const carrier: Record<string, string | undefined> = {};
    propagation.inject(trace.setSpan(context.active(), span), carrier);

    return {
        traceparent: carrier.traceparent ?? '',
        tracestate: carrier.tracestate ?? '',
    };
};

/** Closes the span for a RES response. Wired to ResgateManager's onAfterTrace. */
export const endResSpan = (success: boolean, id: number): void => {
    const span = openSpans.get(id);
    if (span === undefined) return;

    // resclient reports success as "the response carried no error", and a RES
    // error reaches the caller as a rejected promise — a failed operation from
    // the browser's point of view, access denials included.
    if (!success) span.setStatus({ code: SpanStatusCode.ERROR, message: 'RES request failed' });

    span.end();
    openSpans.delete(id);
};

/** Closes anything still open when the socket goes away, so no span leaks unexported. */
export const discardResSpans = (): void => {
    for (const span of openSpans.values()) {
        span.setStatus({ code: SpanStatusCode.ERROR, message: 'connection closed' });
        span.end();
    }

    openSpans.clear();
    pendingOperation = null;
};

/**
 * Names the RES traffic. Every operation resclient sends is covered, reads
 * included — the span is only created once the request reaches the wire, so a
 * cached get costs nothing, and neither does resclient's own housekeeping. Names
 * mirror the RES subject the request is sent on (`call.<rid>.<method>` ->
 * `res.call.<rid>.<method>`), with the tenant and any entity id templated out so
 * they stay aggregatable.
 */
export const instrumentResClient = (client: ResClient): void => {
    const call = client.call.bind(client) as ResMethod;
    const authenticate = client.authenticate.bind(client) as ResMethod;
    const create = client.create.bind(client) as ResMethod;
    const setModel = client.setModel.bind(client) as ResMethod;
    const get = client.get.bind(client) as ResMethod;

    client.call = ((rid: string, method: string, params: unknown) =>
        describing('call', rid, method, () =>
            call(...([rid, method, params] as never[])))) as typeof client.call;

    client.authenticate = ((rid: string, method: string, params: unknown) =>
        describing('auth', rid, method, () =>
            authenticate(...([rid, method, params] as never[])))) as typeof client.authenticate;

    client.create = ((rid: string, params: unknown) =>
        describing('call', rid, 'new', () =>
            create(...([rid, params] as never[])))) as typeof client.create;

    client.setModel = ((rid: string, props: object) =>
        describing('call', rid, 'set', () =>
            setModel(...([rid, props] as never[])))) as typeof client.setModel;

    // client.get sends `subscribe.<rid>` on a cache miss and nothing on a hit, so
    // the action mirrors the subject rather than the method that was called.
    client.get = ((rid: string) =>
        describing('subscribe', rid, null, () =>
            get(...([rid] as never[])))) as typeof client.get;
};
